/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';

import { I18nService } from '@recruitee/i18n';
import { RequisitionsGroupKind, RequisitionStatus } from '@recruitee/requisitions-types';

import { RequisitionsGroupToDisplay } from '../requisitions.types';

type RequisitionsGroupByItem = {
  kind: RequisitionsGroupKind | null;
  text: string;
};
@Injectable()
export class RequisitionsListGroupsService {
  public groupByTranslations = {
    noGroup: this.i18n.translate('app.requisitions.list.group_by.no_group'),
    department: this.i18n.translate('app.requisitions.list.group_by.department'),
    country: this.i18n.translate('app.requisitions.list.group_by.country'),
    status: this.i18n.translate('app.requisitions.list.group_by.status'),
    location: this.i18n.translate('app.requisitions.list.group_by.location'),
    state: this.i18n.translate('app.requisitions.list.group_by.state'),
    recruiter: this.i18n.translate('app.requisitions.list.group_by.recruiter'),
    hiringManager: this.i18n.translate('app.requisitions.list.group_by.hiring_manager'),
    workModel: this.i18n.translate('app.requisitions.list.group_by.work_model'),
  };

  public emptyGroupTranslations = {
    department: this.i18n.translate('app.requisitions.list.table.grouping.without_department'),
    location: this.i18n.translate('app.requisitions.list.table.grouping.without_location'),
    state: this.i18n.translate('app.requisitions.list.table.grouping.without_state'),
    recruiter: this.i18n.translate('app.requisitions.list.table.grouping.without_recruiter'),
    country: this.i18n.translate('app.requisitions.list.table.grouping.without_country'),
    hiringManager: this.i18n.translate(
      'app.requisitions.list.table.grouping.without_hiring_manager',
    ),
    workModel: this.i18n.translate('app.requisitions.list.table.grouping.without_work_model'),
  };

  public requisitionStatusTranslations = {
    draft: this.i18n.translate('app.requisitions.status.draft'),
    pending: this.i18n.translate('app.requisitions.status.pending'),
    rejected: this.i18n.translate('app.requisitions.status.rejected'),
    approved: this.i18n.translate('app.requisitions.status.approved'),
    filled: this.i18n.translate('app.requisitions.status.filled'),
  };

  public groupByItems: RequisitionsGroupByItem[] = [
    { kind: null, text: this.groupByTranslations.noGroup },
    {
      kind: RequisitionsGroupKind.Department,
      text: this.groupByTranslations.department,
    },
    {
      kind: RequisitionsGroupKind.Status,
      text: this.groupByTranslations.status,
    },
    {
      kind: RequisitionsGroupKind.Location,
      text: this.groupByTranslations.location,
    },
    {
      kind: RequisitionsGroupKind.Country,
      text: this.groupByTranslations.country,
    },
    {
      kind: RequisitionsGroupKind.State,
      text: this.groupByTranslations.state,
    },
    {
      kind: RequisitionsGroupKind.WorkModel,
      text: this.groupByTranslations.workModel,
    },
    {
      kind: RequisitionsGroupKind.Recruiter,
      text: this.groupByTranslations.recruiter,
    },
    {
      kind: RequisitionsGroupKind.HiringManager,
      text: this.groupByTranslations.hiringManager,
    },
  ];

  constructor(private i18n: I18nService) {}

  public getGroupButtonLabel(groupBy: RequisitionsGroupKind | null): string {
    switch (groupBy) {
      case RequisitionsGroupKind.Country:
        return this.groupByTranslations.country;
      case RequisitionsGroupKind.Department:
        return this.groupByTranslations.department;
      case RequisitionsGroupKind.Status:
        return this.groupByTranslations.status;
      case RequisitionsGroupKind.Location:
        return this.groupByTranslations.location;
      case RequisitionsGroupKind.State:
        return this.groupByTranslations.state;
      case RequisitionsGroupKind.Recruiter:
        return this.groupByTranslations.recruiter;
      case RequisitionsGroupKind.HiringManager:
        return this.groupByTranslations.hiringManager;
      case RequisitionsGroupKind.WorkModel:
        return this.groupByTranslations.workModel;
      default:
        return this.groupByTranslations.noGroup;
    }
  }

  public fillGroupNamesWithTranslations(
    groups: RequisitionsGroupToDisplay[],
    groupKind: RequisitionsGroupKind,
  ): RequisitionsGroupToDisplay[] {
    return groups.map(group => {
      if (groupKind === RequisitionsGroupKind.Status) {
        return {
          ...group,
          name: this.getStatusTranslation(<RequisitionStatus>group.name),
        };
      }
      if (group?.name) return group;

      return this.fillEmptyGroupName(groupKind, group);
    });
  }

  private fillEmptyGroupName(
    groupKind: RequisitionsGroupKind,
    group: RequisitionsGroupToDisplay,
  ): RequisitionsGroupToDisplay {
    switch (groupKind) {
      case RequisitionsGroupKind.Country:
        return {
          ...group,
          name: this.emptyGroupTranslations.country,
        };
      case RequisitionsGroupKind.Department:
        return {
          ...group,
          name: this.emptyGroupTranslations.department,
        };
      case RequisitionsGroupKind.Location:
        return {
          ...group,
          name: this.emptyGroupTranslations.location,
        };
      case RequisitionsGroupKind.State:
        return {
          ...group,
          name: this.emptyGroupTranslations.state,
        };
      case RequisitionsGroupKind.Recruiter:
        return {
          ...group,
          name: this.emptyGroupTranslations.recruiter,
        };
      case RequisitionsGroupKind.HiringManager:
        return {
          ...group,
          name: this.emptyGroupTranslations.hiringManager,
        };
      case RequisitionsGroupKind.WorkModel:
        return {
          ...group,
          name: this.emptyGroupTranslations.workModel,
        };
      default:
        return group;
    }
  }

  private getStatusTranslation(status: RequisitionStatus): string {
    switch (status) {
      case RequisitionStatus.Draft:
        return this.requisitionStatusTranslations.draft;
      case RequisitionStatus.Pending:
        return this.requisitionStatusTranslations.pending;
      case RequisitionStatus.Rejected:
        return this.requisitionStatusTranslations.rejected;
      case RequisitionStatus.Approved:
        return this.requisitionStatusTranslations.approved;
      case RequisitionStatus.Filled:
        return this.requisitionStatusTranslations.filled;
      default:
        return status;
    }
  }
}
